import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { PlusCircle, X, Info, ChevronDown, Trash2 } from "feather-icons-react/build/IconComponents";
import ImageWithBasePath from "../../core/img/imagewithbasebath";
import {
  createClient,
  updateBrand,
  createBrand,
  getClient,
  updateClient,
  deleteClient,
} from "../../core/api/settings";
import { Button } from "antd";
import Swal from "sweetalert2";
import BrandForm from "./BrandForm";

const ClientForm = ({ clientId, onClientSaved, onClientDeleted, onCancel }) => {
  const [logoRemoved, setLogoRemoved] = useState(false);
  const [headerTitle, setHeaderTitle] = useState("Create New Client");
  const [loading, setLoading] = useState(false);

  const [clientData, setClientData] = useState({
    company_name: "",
    address: "",
    website: "",
    emails: [""],
    logo: null,
    brands: [],
  });

  useEffect(() => {
    const fetchClientData = async () => {
      if (clientId) {
        setLoading(true);
        try {
          const response = await getClient(clientId);
          const client = response.data;

          const normalizedBrands = (client.brands || []).map((brand) => ({
            id: brand.id,
            client: brand.client,
            brand_name: brand.brand_name || "",
            brand_website: brand.brand_website || "",
            brand_emails: brand.brand_emails || [""],
            brand_logo: brand.brand_logo || null,
            return_address: brand.return_address || [""],
            logistics: brand.logistics || [{ name: "", address: "" }],
            banks: brand.banks || [""],
            payee_names: brand.payee_names || [""],
            merchant_accounts: brand.merchant_accounts?.map((account) => ({
              account: account.account || "",
              gateways: account.gateways?.map((g) => ({
                name: g.name || "",
                link: g.link || "",
              })) || [{ name: "", link: "" }],
            })) || [
              {
                account: "",
                gateways: [{ name: "", link: "" }],
              },
            ],
          }));

          const clientDataObj = {
            company_name: client.company_name || "",
            address: client.address || "",
            website: client.website || "",
            emails: client.emails || [""],
            logo: client.logo || null,
            brands: normalizedBrands,
          };

          setClientData(clientDataObj);
          setHeaderTitle(`Update Client - ${client.company_name || ""}`);
        } catch (error) {
          console.error("Failed to fetch client data:", error);
          Swal.fire({
            icon: "error",
            title: "Error",
            text: "Failed to fetch client data. Please try again.",
          });
        } finally {
          setLoading(false);
        }
      } else {
        // Reset form for new client
        setClientData({
          company_name: "",
          address: "",
          website: "",
          emails: [""],
          logo: null,
          brands: [],
        });
        setHeaderTitle("Create New Client");
        setLogoRemoved(false);
      }
    };

    fetchClientData();
  }, [clientId]);

  const handleSaveClient = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const formData = new FormData();
      for (const key in clientData) {
        if (key === "logo") {
          if (logoRemoved) {
            formData.append(key, "");
          } else if (clientData[key] instanceof File) {
            formData.append(key, clientData[key]);
          }
        } else if (Array.isArray(clientData[key])) {
          formData.append(key, JSON.stringify(clientData[key]));
        } else {
          formData.append(key, clientData[key]);
        }
      }

      let clientResponse;
      if (clientId) {
        clientResponse = await updateClient(clientId, formData);
        Swal.fire({
          icon: "success",
          title: "Client Updated",
          text: `${clientResponse.data?.company_name} has been successfully updated.`,
          timer: 3000,
          showConfirmButton: false,
        });
      } else {
        clientResponse = await createClient(formData);
        Swal.fire({
          icon: "success",
          title: "Client Created",
          text: `${clientResponse.data?.company_name} has been successfully created.`,
          timer: 3000,
          showConfirmButton: false,
        });
      }

      if (onClientSaved) {
        onClientSaved(clientResponse.data);
      }
    } catch (error) {
      console.error("Failed to save client data:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to save the client. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClient = async () => {
    if (!clientId) return;

    const result = await Swal.fire({
      title: "Are you sure?",
      text: `Do you want to delete the client "${clientData.company_name}"? This action cannot be undone.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    });

    if (result.isConfirmed) {
      setLoading(true);
      try {
        await deleteClient(clientId);
        Swal.fire({
          icon: "success",
          title: "Client Deleted",
          text: `${clientData.company_name} has been successfully deleted.`,
          timer: 3000,
          showConfirmButton: false,
        });
        if (onClientDeleted) {
          onClientDeleted(clientId);
        }
      } catch (error) {
        console.error("Failed to delete client:", error);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to delete the client. Please try again.",
        });
      } finally {
        setLoading(false);
      }
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setClientData((prevData) => ({ ...prevData, logo: file }));
      setLogoRemoved(false);
    }
  };

  const handleAddEmail = () => {
    setClientData((prevData) => ({
      ...prevData,
      emails: [...prevData.emails, ""],
    }));
  };

  const handleRemoveEmail = (index) => {
    setClientData((prevData) => {
      const updatedEmails = [...prevData.emails];
      updatedEmails.splice(index, 1);
      return { ...prevData, emails: updatedEmails };
    });
  };

  const handleEmailChange = (index, value) => {
    setClientData((prevData) => {
      const updatedEmails = [...prevData.emails];
      updatedEmails[index] = value;
      return { ...prevData, emails: updatedEmails };
    });
  };

  const handleRemoveLogo = () => {
    setClientData((prevData) => ({ ...prevData, logo: null }));
    setLogoRemoved(true);
  };

  const handleAddBrand = () => {
    setClientData((prevData) => ({
      ...prevData,
      brands: [
        ...prevData.brands,
        {
          client: clientId,
          brand_name: "",
          brand_emails: [""],
          return_address: [""],
          brand_website: "",
          brand_logo: null,
          address: "",
          logistics: [
            {
              name: "",
              address: "",
            },
          ],
          banks: [""],
          payee_names: [""],
          merchant_accounts: [
            {
              account: "",
              gateways: [{ name: "", link: "" }],
            },
          ],
        },
      ],
    }));
  };

  const handleRemoveNestedField = (brandIndex, field, index) => {
    setClientData((prevData) => {
      const updatedBrands = [...prevData.brands];
      updatedBrands[brandIndex][field].splice(index, 1);
      return { ...prevData, brands: updatedBrands };
    });
  };

  const handleCancelBrand = (index) => {
    const accordionId = `#collapseOne${index}`;
    const accordionElement = document.querySelector(accordionId);
    if (accordionElement) {
      accordionElement.classList.remove("show");
    }
  };

  const handleSaveBrand = async (index) => {
    const brandData = clientData.brands[index];
    const brandLogoRemoved = brandData.logo === null;
    try {
      const formData = new FormData();
      for (const key in brandData) {
        if (key === "brand_logo") {
          if (brandLogoRemoved) {
            formData.append(key, "");
          } else if (brandData[key] instanceof File) {
            formData.append(key, brandData[key]);
          }
        } else if (Array.isArray(brandData[key]) || typeof brandData[key] === "object") {
          formData.append(key, JSON.stringify(brandData[key]));
        } else {
          formData.append(key, brandData[key]);
        }
      }

      const response = brandData.id
        ? await updateBrand(brandData.id, formData)
        : await createBrand(formData);

      // Update the brand data in state immediately so BrandForm can access the new ID
      setClientData((prevData) => {
        const updatedBrands = [...prevData.brands];
        updatedBrands[index] = response.data;
        return { ...prevData, brands: updatedBrands };
      });

      Swal.fire({
        icon: "success",
        title: "Brand Saved",
        text: `Brand "${response.data.brand_name}" has been successfully saved.`,
        timer: 3000,
        showConfirmButton: false,
      });

      // Return the response so BrandForm can use it for partner copying
      return response;
    } catch (error) {
      console.error("Failed to save brand:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to save the brand. Please try again.",
      });
      throw error; // Re-throw so BrandForm can handle it
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: "400px" }}>
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-12">
          <div className="page-header">
            <div className="add-item d-flex">
              <div className="page-title">
                <h4>{headerTitle}</h4>
                <h6>{clientId ? "Update client information" : "Create a new client"}</h6>
              </div>
            </div>
          </div>

          {/* Client Information */}
          <div className="card">
            <div className="card-body add-product pb-0">
              <div className="accordion-card-one accordion" id="accordionExample">
                <div className="accordion-item">
                  <div className="accordion-header" id="headingOne">
                    <div
                      className="accordion-button"
                      data-bs-toggle="collapse"
                      data-bs-target="#collapseOne"
                      aria-controls="collapseOne">
                      <div className="addproduct-icon">
                        <h5>
                          <Info className="add-info" />
                          <span>
                            {clientId
                              ? "Client: " + clientData?.company_name
                              : "Client Information"}
                          </span>
                        </h5>
                        <Link to="#">
                          <ChevronDown className="chevron-down-add" />
                        </Link>
                      </div>
                    </div>
                    <div
                      id="collapseOne"
                      className="accordion-collapse collapse show"
                      aria-labelledby="headingOne"
                      data-bs-parent="#accordionExample">
                      <div className="accordion-body">
                        <div className="row">
                          {/* Company Name */}
                          <div className="col-lg-6 col-sm-6 col-12">
                            <label className="form-label">Company Name</label>
                            <input
                              type="text"
                              className="form-control"
                              value={clientData?.company_name}
                              onChange={(e) =>
                                setClientData({
                                  ...clientData,
                                  company_name: e.target.value,
                                })
                              }
                              placeholder="Enter Company Name"
                            />
                          </div>

                          {/* Address */}
                          <div className="col-lg-6 col-sm-6 col-12">
                            <label className="form-label">Address</label>
                            <textarea
                              className="form-control"
                              rows="3"
                              value={clientData?.address}
                              onChange={(e) =>
                                setClientData({
                                  ...clientData,
                                  address: e.target.value,
                                })
                              }
                              placeholder="Enter Address"></textarea>
                          </div>
                        </div>
                        <div className="row">
                          <div className="col-lg-6 col-sm-6 col-12">
                            <label className="form-label">Emails</label>
                            <div className="row">
                              {clientData?.emails?.map((email, index) => (
                                <div
                                  key={index}
                                  className="col-md-6 d-flex align-items-center mt-2">
                                  <span className="me-2">{index + 1}.</span>
                                  <input
                                    type="email"
                                    className="form-control"
                                    value={email}
                                    onChange={(e) => handleEmailChange(index, e.target.value)}
                                    placeholder="Enter Email"
                                  />
                                  <button
                                    type="button"
                                    className="btn btn-outline-secondary btn-sm ms-2"
                                    onClick={() => handleRemoveEmail(index)}>
                                    <X size={16} />
                                  </button>
                                </div>
                              ))}
                            </div>
                            <button
                              type="button"
                              className="btn btn-outline-primary mt-2"
                              onClick={handleAddEmail}>
                              <PlusCircle className="me-2 iconsize" />
                              Add Email
                            </button>
                          </div>
 
                        </div>
                        {/* Website */}
                        <div className="row">
                          <div className="col-lg-6 col-sm-6 col-12">
                            <label className="form-label">Website</label>
                            <input
                              type="text"
                              className="form-control"
                              value={clientData?.website}
                              onChange={(e) =>
                                setClientData({
                                  ...clientData,
                                  website: e.target.value,
                                })
                              }
                              placeholder="Enter Website URL"
                            />
                          </div>
                        </div>

                        <div className="d-flex justify-content-between mt-3">
                          <div>
                            {clientId && (
                              <button
                                type="button"
                                className="btn btn-danger"
                                onClick={handleDeleteClient}
                                disabled={loading}>
                                <Trash2 className="me-2" size={16} />
                                Delete Client
                              </button>
                            )}
                          </div>
                          <div>
                            <button
                              type="button"
                              className="btn btn-secondary me-2"
                              onClick={onCancel}
                              disabled={loading}>
                              Cancel
                            </button>
                            <button
                              type="submit"
                              className="btn btn-primary"
                              onClick={handleSaveClient}
                              disabled={loading}>
                              {loading ? (
                                <>
                                  <span
                                    className="spinner-border spinner-border-sm me-2"
                                    role="status"></span>
                                  {clientId ? "Updating..." : "Creating..."}
                                </>
                              ) : clientId ? (
                                "Update Client"
                              ) : (
                                "Create Client"
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {clientData?.brands?.map((brandData, index) => (
            <BrandForm
              key={index}
              brandData={brandData}
              index={index}
              setClientData={setClientData}
              onCancelBrand={handleCancelBrand}
              onSaveBrand={handleSaveBrand}
              onRemoveNestedField={handleRemoveNestedField}
              fetchClients={onClientSaved}
            />
          ))}

          {clientId && (
            <Button
              type="dashed"
              onClick={handleAddBrand}
              className="mt-2"
              icon={<PlusCircle size={18} />}
              block>
              Add Brand
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClientForm;
